/**
 * Public bot profile (§7.2), reached from Browse.
 *
 * The health block is not decoration: 30-day uptime, the count of rentals that
 * ended because the connected account failed, and how fast the owner typically
 * responds to a re-auth prompt are the three numbers that predict whether a
 * rental will actually work. They are shown before the request, not after.
 */

import { Link, useParams } from 'react-router-dom';

import { useApi } from '../hooks/useApi';
import { routes } from '../api/routes';
import type { BotHealth, Listing } from '../api/types';
import { useSession } from '../session/SessionContext';
import { Card, Loading, Stat, Badge } from '../components/ui';
import { ErrorView } from '../components/ErrorView';
import { EmptyState } from '../components/EmptyState';
import { amountLabel, duration, localDate, percent, periodLabel } from '../lib/format';
import { ASKING_LABEL, OWNER_LABEL, REQUEST_LABEL_LONG } from '../lib/vocabulary';

export function BotProfile() {
  const { listingId = '' } = useParams();
  const { authenticated } = useSession();
  const listing = useApi<Listing>(routes.listings.one(listingId));
  const botId = listing.data?.bot_id ?? null;
  const health = useApi<BotHealth>(botId ? routes.bots.health(botId) : null);
  // TODO(api): reviews are only readable per rental (GET /rentals/{id}/reviews),
  // and this screen has no rental to key off — a stranger reading a listing is
  // not party to any of them. The rating summary on the listing is what §10.4
  // actually exposes here. It needs GET /api/v1/bots/{id}/reviews.

  if (listing.initialLoading) return <Loading label="Loading this listing" />;
  if (listing.problem) return <ErrorView problem={listing.problem} onRetry={listing.reload} />;
  if (!listing.data) return null;

  const l = listing.data;

  return (
    <div className="profile">
      <header className="page-head">
        <h1>{l.bot_name}</h1>
        <p className="muted">
          {l.platform} · {OWNER_LABEL} @{l.owner_handle}
        </p>
      </header>

      <div className="profile-grid">
        <Card title="Terms">
          <dl className="stats">
            <Stat
              label={ASKING_LABEL}
              value={amountLabel(l.asking)}
              hint={`per ${periodLabel(l.period_count, l.period_unit)}, set by the ${OWNER_LABEL.toLowerCase()}`}
            />
            <Stat
              label="Length"
              value={`${l.min_periods}–${l.max_periods} × ${periodLabel(l.period_count, l.period_unit)}`}
            />
            <Stat
              label="Included generation credits"
              value={`${l.included_generation_credits.text} text · ${l.included_generation_credits.image} image`}
              hint={`Anything beyond that is billed to the ${l.generation_overage_bearer}.`}
            />
            <Stat
              label="Next free"
              value={l.next_available_at ? localDate(l.next_available_at) : 'Now'}
            />
          </dl>

          <h3>Permissions this listing grants</h3>
          <ul className="perm-chips">
            {l.granted_permissions.map((p) => (
              <li key={p} className="chip chip-static">
                {p.replace(/_/g, ' ')}
              </li>
            ))}
          </ul>

          {l.terms ? (
            <>
              <h3>{OWNER_LABEL}&rsquo;s terms</h3>
              <p className="preview">{l.terms}</p>
            </>
          ) : null}

          {l.status === 'active' ? (
            authenticated ? (
              <Link className="btn btn-primary" to={`/listings/${l.id}/request`}>
                {REQUEST_LABEL_LONG}
              </Link>
            ) : (
              <Link className="btn btn-primary" to="/login">
                Sign in to request
              </Link>
            )
          ) : (
            <p className="muted">
              This listing is {l.status}
              {l.paused_reason ? ` — ${l.paused_reason}` : ''}.
            </p>
          )}
        </Card>

        <Card title="Track record">
          {health.initialLoading ? <Loading label="Loading health" /> : null}
          {health.problem ? <ErrorView problem={health.problem} onRetry={health.reload} /> : null}
          {health.data ? (
            <dl className="stats">
              <Stat
                label="Health score"
                value={
                  <Badge
                    tone={
                      health.data.health_score > 0.9
                        ? 'ok'
                        : health.data.health_score > 0.7
                          ? 'warn'
                          : 'bad'
                    }
                  >
                    {percent(health.data.health_score, 0)}
                  </Badge>
                }
              />
              <Stat label="Uptime, last 30 days" value={percent(health.data.uptime_30d, 1)} />
              <Stat
                label="Rentals ended by account failure"
                value={health.data.account_failure_terminations}
                hint="Times a connected account was suspended mid-rental."
              />
              <Stat
                label={`Median ${OWNER_LABEL.toLowerCase()} re-auth response`}
                value={
                  health.data.median_owner_reauth_seconds === null
                    ? 'never needed'
                    : duration(health.data.median_owner_reauth_seconds)
                }
                hint="How quickly this owner restores a broken connection."
              />
            </dl>
          ) : null}
        </Card>
      </div>

      <Card title="Reviews">
        {l.rating_count > 0 ? (
          <dl className="stats">
            <Stat label="Rating" value={`${l.rating_avg.toFixed(1)}/5`} />
            <Stat
              label="Reviews counted"
              value={String(l.rating_count)}
              hint="Only renter reviews that were revealed count toward this figure."
            />
          </dl>
        ) : (
          <EmptyState
            heading="No reviews yet"
            body="Reviews appear after a rental ends and both sides have written one, or after the 14-day window closes."
          />
        )}
      </Card>
    </div>
  );
}
